import { useSelector } from "react-redux";
import * as _ from "lodash";

import { Enterprise, DataDescriptorEntity, EnterpriseAppState, FarmEntity } from "@ctra/api";
import { useFarmList } from "@farms";

/**
 * Tell which of the given farms have support for the given KPI
 * @param dataDescriptorID
 * @param farmIDList
 */
export const useKPISupport = (
  dataDescriptorID: DataDescriptorEntity["id"] | Array<DataDescriptorEntity["id"]>,
  farmIDList: Array<FarmEntity["id"]> = []
): Record<"hasSupport" | "hasNoSupport", Array<FarmEntity["id"]>> => {
  const { farmList } = useFarmList();

  /**
   * Resolve data descriptors from data descriptor id
   * @type {DataDescriptorEntity[]}
   */
  const dataDescriptorEntities: DataDescriptorEntity[] = useSelector<
    EnterpriseAppState,
    DataDescriptorEntity[]
  >((state) => Enterprise.entities.getDataDescriptor(state, { id: _.concat([], dataDescriptorID) }));

  /**
   * Make a list of all available farms
   * @type {FarmEntity["id"][]}
   */
  const availableFarmIDList: FarmEntity["id"][] = _.map(farmList, "id");

  /**
   * List of supported farms (which every metric supports equally)
   * @type {FarmEntity["id"][]}
   */
  const supportedFarms: FarmEntity["id"][] = _.intersection(
    ..._.map(dataDescriptorEntities, "supportedFarms")
  );

  /**
   * Tell which farms support the KPI
   * @type {FarmEntity["id"][]}
   */
  const supportedBy: FarmEntity["id"][] = _.intersection(
    _.isEmpty(farmIDList) ? availableFarmIDList : farmIDList,
    supportedFarms
  );

  /**
   * Tell which farms does not support the KPI
   * @type {FarmEntity["id"][]}
   */
  const blockedBy: FarmEntity["id"][] = _.difference(
    _.isEmpty(farmIDList) ? availableFarmIDList : farmIDList,
    supportedFarms
  );

  return {
    hasSupport: supportedBy,
    hasNoSupport: blockedBy
  };
};
